import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Stack,
  IconButton,
  Autocomplete,
  Chip
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { MediaManager } from "./MediaManager";
import { useUpdateProduct, useCreateProduct } from "./hooks/useProductActions";
import { useFetchBrands } from "./hooks/useFetchBrands";

const defaultFormData = {
  name: "",
  title: "",
  brandId: "",
  brandName: "",
  description: "",
  canonicalUrl: "",
  affiliateUrl: "",
  price: "",
  tags: [],
  media: []
};

export const ProductsDrawer = ({
  open,
  onClose,
  product,
  isEditing = false
}) => {
  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});

  const updateProductMutation = useUpdateProduct();
  const createProductMutation = useCreateProduct();
  const { data: brandsData } = useFetchBrands();

  useEffect(() => {
    if (product && isEditing) {
      setFormData({
        ...defaultFormData,
        ...product
      });
    } else if (!isEditing) {
      setFormData(defaultFormData);
    }
    setErrors({});
  }, [product, isEditing, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleBrandChange = (brandId) => {
    const selectedBrand = brandsData?.brands?.find(
      brand => brand.id === brandId
    );

    setFormData(prev => ({
      ...prev,
      brandId,
      brandName: selectedBrand?.Vendor?.vendorName || selectedBrand?.vendorName || selectedBrand?.brandName || selectedBrand?.name || ""
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.canonicalUrl?.trim()) {
      try {
      // eslint-disable-next-line no-new
        new URL(formData.canonicalUrl);
      } catch {
        newErrors.canonicalUrl = "Please enter a valid URL";
      }
    }

    if (formData.affiliateUrl && formData.affiliateUrl.trim()) {
      try {
        // eslint-disable-next-line no-new
        new URL(formData.affiliateUrl);
      } catch {
        newErrors.affiliateUrl = "Please enter a valid URL";
      }
    }

    if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
      newErrors.price = "Please enter a valid price";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) { return; }

    const submitData = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null
    };

    try {
      if (isEditing) {
        await updateProductMutation.mutateAsync({
          id: product.id,
          ...submitData
        });
      } else {
        await createProductMutation.mutateAsync(submitData);
      }
      onClose();
    } catch (error) {
      console.error("Failed to save product:", error);
    }
  };

  const isLoading = updateProductMutation.isLoading || createProductMutation.isLoading;
  const error = updateProductMutation.error || createProductMutation.error;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 600,
          maxWidth: "90vw"
        }
      }}
    >
      <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3
        }}>
          <Typography variant="h5">
            {isEditing ? "Edit Product" : "Create New Product"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        <Stack spacing={3}>
          <TextField
            label="Name *"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />

          <TextField
            label="Overline"
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            fullWidth
          />

          <Autocomplete
            options={brandsData?.brands || []}
            value={brandsData?.brands?.find(brand => brand.id === formData.brandId) || null}
            onChange={(event, newValue) => {
              handleBrandChange(newValue?.id || "");
            }}
            getOptionLabel={(option) =>
              option.Vendor?.vendorName || option.vendorName || option.brandName || option.name || ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Brand"
                placeholder="Search brands..."
              />
            )}
            fullWidth
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
            multiline
            rows={4}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={formData.tags || []}
            onChange={(event, newValue) => {
              handleInputChange("tags", newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Add tags..."
                helperText="Press Enter to add a tag"
              />
            )}
            fullWidth
          />

          <TextField
            label="Canonical URL"
            value={formData.canonicalUrl}
            onChange={(e) => handleInputChange("canonicalUrl", e.target.value)}
            error={!!errors.canonicalUrl}
            helperText={errors.canonicalUrl}
            fullWidth
          />

          <TextField
            label="Affiliate URL"
            value={formData.affiliateUrl}
            onChange={(e) => handleInputChange("affiliateUrl", e.target.value)}
            error={!!errors.affiliateUrl}
            helperText={errors.affiliateUrl}
            fullWidth
          />

          <TextField
            label="Price"
            value={formData.price}
            onChange={(e) => handleInputChange("price", e.target.value)}
            error={!!errors.price}
            helperText={errors.price}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            fullWidth
          />

          <Divider />

          <MediaManager
            media={formData.media}
            onChange={(media) => handleInputChange("media", media)}
          />

          <Box sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            pt: 2,
            borderTop: 1,
            borderColor: "divider"
          }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : (isEditing ? "Update Product" : "Create Product")}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Drawer>
  );
};
